import React from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { ENV } from '@/config/constants';
import useAuthStore from '@/stores/authStore';

const IntroductionBenefitFeePcPage = () => {
  const navigate = useNavigate();
  const accessToken = useAuthStore((state) => state.accessToken);
  const isLoggedIn = !!accessToken;
  
  // Brand IDs based on environment
  const brandIds = ENV === 'prod' 
    ? [
        '9eb135c2-3376-4df7-8e2f-123d604f10f7', // Brand 1
        '01990f2d-372a-7158-8a9c-da088876707d', // Brand 2
        '019951e5-2b2a-74de-bac8-69ec3fb58f18', // Brand 3
        '0199129c-3541-73ae-be22-37d3478ab3fc', // Brand 4
      ]
    : [
        '9eb135c2-3376-4df7-8e2f-123d604f10f7', // Brand 1
        '019a0098-a9b6-79a5-8e75-e4f314f1d874', // Brand 2
        '019a2577-f4cb-7f89-bba2-4404d56fc765', // Brand 3
        '01992968-5b31-7b51-8537-8108df7bb4a2', // Brand 4
      ];

  const handleBrandClick = (brandId) => {
    navigate(`/brands/${brandId}`);
  };

  const handleRegisterClick = () => {
    if (!isLoggedIn) {
      navigate('/signup');
    }
  };

  return (
    <div className="min-h-screen w-full bg-[#f4f4f4] overflow-x-hidden">
      <style jsx>{`
        img {
          user-drag: none;
          -webkit-user-drag: none;
        }
      `}</style>
      
      <Header />
      
      {/* Hero Section */}
      <section className="w-full relative">
        <img
          src="/images/introduction/benefit_banner.png"
          alt="DONDONスタートガイド for ビギナーズ"
          className="w-full h-auto object-cover"
          draggable="false"
        />
      </section>

      {/* Campaign Card Section */}
      <section className="w-full bg-white h-[183px] flex flex-col items-center justify-center">
        <div className="w-[896px] flex flex-col items-start gap-5">
          {/* First Row */}
          <div className="h-9 flex items-center">
            <div className="w-1 bg-[#AAAAAA] rounded-[2px] h-full"></div>
            <span className="ml-[10px] font-bold text-2xl text-black font-['Noto_Sans_JP']">
              キャンペーン内容
            </span>
          </div>
          
          {/* Second Row */}
          <div className="text-lg text-black font-['Noto_Sans_JP']">
            <span className="font-normal">本キャンペーンでは、会員登録後初めて出品した方に</span>
            <span className="font-bold text-[#FF6A00]">もれなく500円分のボーナス金をプレゼントします！</span>
          </div>
        </div>
      </section>

      {/* Second Section */}
      <section className="w-full bg-[#FFF8F2] h-[766px] py-[60px] flex flex-col items-center gap-8">
        <div className="w-[896px] flex flex-col gap-8">
          {/* First Row - Title */}
          <div className="h-9 flex items-center">
            <div className="w-1 bg-[#FF6A00] rounded-[2px] h-full"></div>
            <span className="ml-[10px] font-bold text-2xl text-black font-['Noto_Sans_JP']">
              キャンペーン参加方法
            </span>
          </div>

          {/* Cards Container */}
          <div className="flex flex-col gap-0">
            {/* First Card - Step 1: Member Registration with bottom image */}
            <div className="flex flex-col">
              <div className="w-[896px] h-[178px] bg-white border border-[#FF6A00] rounded-lg flex items-center gap-6 p-0 overflow-hidden relative">
                {/* Left: Orange square with number and text */}
                <div className="bg-[#FF6A00] rounded-l-lg flex flex-col items-center justify-center w-[200px] h-[178px] flex-shrink-0">
                  <img
                    src="/images/introduction/benefit_fee_one.svg"
                    alt="1"
                    className="w-[19px] h-[47px] mb-[10px] object-contain"
                    draggable="false"
                  />
                  <span className="text-white font-['Noto_Sans_JP'] font-bold text-2xl leading-[150%]">会員登録</span>
                </div>
                
                {/* Right: Text, link and logos */}
                <div className="flex-1 flex items-center ml-[15px]">
                  <div className="font-['Noto_Sans_JP'] font-normal text-lg leading-[150%] text-[#111111]">
                    まずは無料の会員登録！<br/>
                    メールアドレスがあればすぐに始められます。
                  </div>
                </div>

                {/* Circular button on the right */}
                {!isLoggedIn && (
                  <div 
                    className="absolute right-[37px] top-1/2 -translate-y-1/2 w-[138px] h-[138px] rounded-full flex items-center justify-center px-6 bg-[#FF6A00] cursor-pointer hover:bg-[#AAAAAA] active:bg-[#AAAAAA] transition-colors"
                    onClick={handleRegisterClick}
                  >
                    <span className="text-white font-bold text-center font-['Noto_Sans_JP'] text-lg leading-[150%]">
                      会員登録は<br/>こちらから
                    </span>
                  </div>
                )}
              </div>

              {/* Bottom decoration image */}
              <div className="flex justify-center relative z-10">
                <img
                  src="/images/introduction/benefit_card_bottom.svg"
                  alt=""
                  className="h-auto"
                  draggable="false"
                />
              </div>
            </div>

            {/* Second Card - Step 2: First Listing with bottom image */}
            <div className="flex flex-col -mt-[4px] z-0">
              <div className="w-[896px] h-[258px] bg-white border border-[#FF6A00] rounded-lg flex items-center gap-6 p-0 overflow-hidden relative">
                {/* Left: Orange square with number and text */}
                <div className="bg-[#FF6A00] rounded-l-lg flex flex-col items-center justify-center w-[200px] h-[258px] flex-shrink-0">
                  <img
                    src="/images/introduction/benefit_fee_two.svg"
                    alt="2"
                    className="w-[30px] h-[47px] mb-[10px] object-contain"
                    draggable="false"
                  />
                  <span className="text-white font-['Noto_Sans_JP'] font-bold text-2xl leading-[150%]">初出品</span>
                </div>
                
                {/* Right: Text, link and logos */}
                <div className="flex-1 flex flex-col ml-[15px]">
                  <div className="font-['Noto_Sans_JP'] font-normal text-lg leading-[150%] text-[#111111]">
                    出品は1点からOK！お好きなブランド・カテゴリで参加できます。
                  </div>
                  
                  {/* Content area below text */}
                  <div className="w-[622px] h-[151px] bg-[#F4F4F4] rounded-[10px] mt-5 pt-4 px-6 pb-6">
                    <div className="flex flex-col items-center">
                      <div className="text-center font-['Noto_Sans_JP'] font-bold text-sm leading-[150%] text-[#111111]">
                        商品一覧を見る
                      </div>
                      
                      {/* Second row: Four brand images */}
                      <div className="flex gap-[10px] mt-[10px]">
                        <div
                          className="group w-[136px] h-[80px] bg-white rounded-[5px] border border-[#AAAAAA] flex items-center justify-center cursor-pointer"
                          style={{ boxShadow: '2px 2px 0px 0px #0000001A' }}
                          onClick={() => handleBrandClick(brandIds[0])}
                        >
                          <img
                            src="/images/introduction/benefit_brand_card1.png"
                            alt="Brand 1"
                            className="w-[120px] h-[66px] object-contain group-hover:scale-95 group-active:scale-90 transition-transform"
                            draggable="false"
                          />
                        </div>
                        <div
                          className="group w-[136px] h-[80px] bg-white rounded-[5px] border border-[#AAAAAA] flex items-center justify-center cursor-pointer"
                          style={{ boxShadow: '2px 2px 0px 0px #0000001A' }}
                          onClick={() => handleBrandClick(brandIds[1])}
                        >
                          <img
                            src="/images/introduction/benefit_brand_card2.png"
                            alt="Brand 2"
                            className="w-[100px] h-[28px] object-contain group-hover:scale-95 group-active:scale-90 transition-transform"
                            draggable="false"
                          />
                        </div>
                        <div
                          className="group w-[136px] h-[80px] bg-white rounded-[5px] border border-[#AAAAAA] flex items-center justify-center cursor-pointer"
                          style={{ boxShadow: '2px 2px 0px 0px #0000001A' }}
                          onClick={() => handleBrandClick(brandIds[2])}
                        >
                          <img
                            src="/images/introduction/benefit_brand_card3.png"
                            alt="Brand 3"
                            className="w-[100px] h-[49px] object-contain group-hover:scale-95 group-active:scale-90 transition-transform"
                            draggable="false"
                          />
                        </div>
                        <div
                          className="group w-[136px] h-[80px] bg-white rounded-[5px] border border-[#AAAAAA] flex items-center justify-center cursor-pointer"
                          style={{ boxShadow: '2px 2px 0px 0px #0000001A' }}
                          onClick={() => handleBrandClick(brandIds[3])}
                        >
                          <img
                            src="/images/introduction/benefit_brand_card4.png"
                            alt="Brand 4"
                            className="w-[100px] h-[52px] object-contain group-hover:scale-95 group-active:scale-90 transition-transform"
                            draggable="false"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Bottom decoration image */}
              <div className="flex justify-center relative z-10">
                <img
                  src="/images/introduction/benefit_card_bottom.svg"
                  alt=""
                  className="h-auto"
                  draggable="false"
                />
              </div>

              {/* Background image - fixed position at -14px below triangle */}
              <div className="flex justify-center -mt-[14px] relative z-0">
                <img
                  src="/images/introduction/benefit_dot.png"
                  alt=""
                  className="w-[857px] h-[115px]"
                  draggable="false"
                />
              </div>

              {/* Third content area */}
              <div className="flex flex-col items-center relative z-10 -mt-[85px]">
                {/* First row */}
                <div className="font-['Noto_Sans_JP'] font-normal text-lg leading-[150%] text-[#111111]">
                  売上金と同様に使用できる
                </div>

                {/* Second row */}
                <div className="font-['Noto_Sans_JP'] font-bold text-2xl leading-[150%] text-[#FF6A00]">
                  500円分のボーナス金をGET
                </div>

                {/* Third row */}
                <div className="mt-[10px] font-['Noto_Sans_JP'] font-normal text-sm leading-[150%] text-[#111111]">
                  出品完了後すぐにボーナス金が付与されます。「マイページ→ 売上・振込申請管理」ページにてご確認ください。
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Third Section */}
      <section className="w-full bg-white h-[527px] py-[60px] flex flex-col items-center">
        <div className="w-[896px] flex flex-col">
          {/* First Row - Title */}
          <div className="h-9 flex items-center">
            <div className="w-1 bg-[#AAAAAA] rounded-[2px] h-full"></div>
            <span className="ml-[10px] font-bold text-2xl text-black font-['Noto_Sans_JP']">
              注意事項
            </span>
          </div>

          {/* Second Row - Unordered List */}
          <ul className="mt-5 list-none flex flex-col gap-0 font-['Noto_Sans_JP'] font-normal text-lg leading-[150%] text-[#111111]">
            <li className="flex items-start">
              <div className="w-[27px] h-[27px] flex items-center justify-center flex-shrink-0">
                <div className="w-[5px] h-[5px] bg-[#111111] rounded-full"></div>
              </div>
              <span>本ボーナス金の付与は、各ユーザー1回限りとなります。</span>
            </li>
            <li className="flex items-start">
              <div className="w-[27px] h-[27px] flex items-center justify-center flex-shrink-0">
                <div className="w-[5px] h-[5px] bg-[#111111] rounded-full"></div>
              </div>
              <span>ボーナス金のみでの振込申請はできません。売上金と合算して1,000円以上となった場合に振込申請が可能です。なお、ボーナス金は商品の購入や注文金額の割引には利用できません。</span>
            </li>
            <li className="flex items-start">
              <div className="w-[27px] h-[27px] flex items-center justify-center flex-shrink-0">
                <div className="w-[5px] h-[5px] bg-[#111111] rounded-full"></div>
              </div>
              <span>ボーナス金を出金する際は、売上金の振込申請と同じ条件が適用されます。。</span>
            </li>
            <li className="flex items-start">
              <div className="w-[27px] h-[27px] flex items-center justify-center flex-shrink-0">
                <div className="w-[5px] h-[5px] bg-[#111111] rounded-full"></div>
              </div>
              <span>ボーナス金の受取後72時間以内に該当商品を削除した場合、ボーナス金は自動的に取消が行われます。取消の反映にはタイムラグが生じる場合があります。 詳細は「売上・振込申請管理」にてご確認ください。</span>
            </li>
            <li className="flex items-start">
              <div className="w-[27px] h-[27px] flex items-center justify-center flex-shrink-0">
                <div className="w-[5px] h-[5px] bg-[#111111] rounded-full"></div>
              </div>
              <span>重複アカウントや規約違反に該当する出品等が確認された場合、ボーナス金の取消および回収を行う場合があります。</span>
            </li>
            <li className="flex items-start">
              <div className="w-[27px] h-[27px] flex items-center justify-center flex-shrink-0">
                <div className="w-[5px] h-[5px] bg-[#111111] rounded-full"></div>
              </div>
              <span>ボーナス金の有効期限は付与日から180日間です。期限を過ぎると自動的に失効しますのでご注意ください。</span>
            </li>
            <li className="flex items-start">
              <div className="w-[27px] h-[27px] flex items-center justify-center flex-shrink-0">
                <div className="w-[5px] h-[5px] bg-[#111111] rounded-full"></div>
              </div>
              <span>ボーナス金の利用規則は、運営方針により変更または終了する場合があります。</span>
            </li>
            <li className="flex items-start">
              <div className="w-[27px] h-[27px] flex items-center justify-center flex-shrink-0">
                <div className="w-[5px] h-[5px] bg-[#111111] rounded-full"></div>
              </div>
              <span>本キャンペーンの開始および終了時期は、運営状況により変更となる場合があります。詳細は、バナーまたはメッセージセンターにて通知が行われます。</span>
            </li>
          </ul>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default IntroductionBenefitFeePcPage;

